import { api, fetchBlobUrl } from "@/lib/api";
import type { RoleCode, UserSummary } from "@/features/auth/types";

export interface EmployeeInfoFields {
  employee_code?: string | null;
  joining_date?: string | null;
  date_of_birth?: string | null;
  blood_group?: string | null;
  address?: string | null;
  emergency_contact?: string | null;
}

// A site a staff member is scoped to (site-first hierarchy). The full Site
// entity (with code/customer_count) lives in features/sites.
export interface AssignedSite {
  id: string;
  name: string;
}

export interface ManagedUser extends EmployeeInfoFields {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  designation: string | null;
  role_code: RoleCode;
  role_name: string;
  department_id: string | null;
  department_name: string | null;
  has_photo: boolean;
  sites: AssignedSite[];
  customer_id: string | null;
  is_active: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface Department {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  user_count: number;
}

export interface Role {
  id: string;
  code: RoleCode;
  name: string;
  description: string | null;
  is_system: boolean;
  permissions: string[];
  user_count: number;
}

export interface UserFilters {
  page?: number;
  page_size?: number;
  search?: string;
  role_code?: string;
  department_id?: string;
  is_active?: boolean;
  account_type?: "internal" | "customer";
}

export function listUsers(filters: UserFilters = {}): Promise<Page<ManagedUser>> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    // Empty strings must not become `role_code=` — the API would treat that
    // as a filter for the empty role and return nothing.
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const query = qs.toString();
  return api.get<Page<ManagedUser>>(`/users${query ? `?${query}` : ""}`);
}

export function getUser(id: string): Promise<ManagedUser> {
  return api.get<ManagedUser>(`/users/${id}`);
}

export interface CreateUserBody extends EmployeeInfoFields {
  email: string;
  password: string;
  full_name: string;
  role_code: string;
  phone?: string | null;
  designation?: string | null;
  department_id?: string | null;
  customer_id?: string | null;
  site_ids?: string[];
}

export function createUser(
  body: CreateUserBody,
): Promise<{ user: ManagedUser; temporary_password: string }> {
  return api.post<{ user: ManagedUser; temporary_password: string }>("/users", body);
}

export function updateUser(
  id: string,
  body: Partial<Omit<CreateUserBody, "email" | "password">> & { is_active?: boolean },
): Promise<ManagedUser> {
  return api.patch<ManagedUser>(`/users/${id}`, body);
}

export function deleteUser(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/users/${id}`);
}

export function uploadUserPhoto(id: string, file: File): Promise<ManagedUser> {
  const form = new FormData();
  form.append("file", file);
  return api.upload<ManagedUser>(`/users/${id}/photo`, form);
}

/** Fetch a user's photo as an object URL (auth-protected). Revoke when done. */
export function userPhotoUrl(id: string): Promise<string> {
  return fetchBlobUrl(`/users/${id}/photo`);
}

export function resetPassword(
  id: string,
): Promise<{ temporary_password: string; message: string }> {
  return api.post<{ temporary_password: string; message: string }>(
    `/users/${id}/reset-password`,
  );
}

export function setPassword(
  id: string,
  body: { new_password: string; must_change: boolean },
): Promise<{ message: string }> {
  return api.post<{ message: string }>(`/users/${id}/set-password`, body);
}

export function listRoles(): Promise<Role[]> {
  return api.get<Role[]>("/roles");
}

export function listDepartments(): Promise<Department[]> {
  return api.get<Department[]>("/departments");
}

export interface DepartmentBody {
  name: string;
  description?: string | null;
}

export function createDepartment(body: DepartmentBody): Promise<Department> {
  return api.post<Department>("/departments", body);
}

export function updateDepartment(
  id: string,
  body: Partial<DepartmentBody> & { is_active?: boolean },
): Promise<Department> {
  return api.patch<Department>(`/departments/${id}`, body);
}

export function deleteDepartment(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/departments/${id}`);
}

export type { UserSummary };
